import { Accordion, AccordionDetails, Stack } from '@mui/material';
import { NameValueDisplay, VerificationStatus } from '@verifime/components';
import tokens from '@verifime/design-tokens';
import { formatToDDMMMYYYYHHMMAAA } from '@verifime/utils';
import { default as AccordionTitle } from '../AccordionTitle';
import AdverseMediaHistory from './AdverseMediaHistory';
import { TAdverseMediaStatus } from './types';
import { latestRecord } from './utils';

export type TAdverseMediaDetails = {
  history: Partial<TAdverseMediaStatus>[];
};

/**
 * AdverseMediaSection Component
 * A collapsible accordion component that displays the summary and detailed history
 * of Adverse Media screenings for a customer.
 */
export default function AdverseMediaSection({ history }: Partial<TAdverseMediaDetails>) {
  // Do not render the component if there is no history data.
  if (!history || history.length === 0) {
    return null;
  }

  // Determine the overall status and last checked date from the most recent record.
  // The history array is expected to be sorted by date descending from the API.
  const latest = latestRecord(history);
  const isMatched = latest.adverseMediaStatus;
  const lastCheckedDate = latest.lastUpdatedDatetime;

  return (
    // The accordion is expanded by default if there is a match, as per UX requirements.
    <Accordion
      defaultExpanded={isMatched}
      data-testid="adverse-media-section"
      data-component="adverse-media-section"
      data-record-status={isMatched ? 'matched' : 'not-matched'}
    >
      <AccordionTitle
        title="Adverse Media Screening"
        displayStatus={<VerificationStatus status={isMatched ? 'Matched' : 'NotMatched'} />}
        data-testid="adverse-media-section-title"
      />
      <AccordionDetails>
        <Stack gap={tokens.spacingXs} direction="column">
          <NameValueDisplay
            displayType="horizontal"
            name="Last checked"
            value={formatToDDMMMYYYYHHMMAAA(lastCheckedDate)}
            data-testid="adverse-media-last-checked"
          />
          {/* Render the detailed history table */}
          <AdverseMediaHistory history={history} />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
